import React, { useState, useEffect } from 'react';
import Select, { SingleValue } from 'react-select';
import { themaSelectThema } from './shared/StyledSelect';
import { useTranslation } from 'react-i18next';
import { Match, Lineup, LineupPosition } from '../types/api';
import { apiService } from '../services/api';
import PlayerCircle from './PlayerCircle';
import { veldPositieStijl, VELD_MIDDEN_STIJL } from '../utils/veldPositie';

interface HockeyFieldBackgroundProps {
  match: Match | null;
  selectedLineup?: Lineup | null;
  onLineupChange?: (lineup: Lineup | null) => void;
  /**
   * Deel-link context van het team. Dit component draait op de publieke
   * spelerspagina, waar de bezoeker niet is ingelogd. De opstellingen moeten daarom
   * via /public/* worden opgehaald - het generieke /lineups-endpoint is afgeschermd
   * omdat matchId een oplopend integer is en dus af te lopen valt.
   */
  publicId?: string | null;
  teamSlug?: string | null;
}

const HockeyFieldBackground: React.FC<HockeyFieldBackgroundProps> = ({
  match,
  selectedLineup: externalSelectedLineup,
  onLineupChange,
  publicId,
  teamSlug
}) => {
  const { t } = useTranslation(['playerView', 'common']);
  const [lineups, setLineups] = useState<Lineup[]>([]);
  const [internalSelectedLineup, setInternalSelectedLineup] = useState<Lineup | null>(null);
  const [loading, setLoading] = useState(true);
  // De sleutel wordt bewaard, niet de vertaalde zin: anders blijft een foutmelding die
  // al op het scherm staat in de oude taal hangen na een taalwissel.
  const [errorKey, setErrorKey] = useState<string | null>(null);

  // Use external selectedLineup if provided, otherwise use internal state
  const selectedLineup = externalSelectedLineup !== undefined ? externalSelectedLineup : internalSelectedLineup;

  useEffect(() => {
    const loadLineupsAndFormation = async () => {
      if (!match) return;

      if (!publicId || !teamSlug) {
        setErrorKey('errors.expiredLink');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorKey(null);

        const lineupsData = await apiService.getPublicLineups(publicId, teamSlug, match.matchId);
        setLineups(lineupsData);
        
        // Select first lineup by default if no external control
        if (lineupsData.length > 0) {
          if (onLineupChange && !externalSelectedLineup) {
            onLineupChange(lineupsData[0]);
          } else if (!onLineupChange) {
            setInternalSelectedLineup(lineupsData[0]);
          }
        }
      } catch (err) {
        setErrorKey('lineup.loadFailed');
        console.error('Error loading lineups and formation:', err);
      } finally {
        setLoading(false);
      }
    };

    loadLineupsAndFormation();
  }, [match, externalSelectedLineup, onLineupChange, publicId, teamSlug]);


  const getFieldPlayers = (): LineupPosition[] => {
    if (!selectedLineup) return [];
    // Show players who have a formationPosition assignment (not null)
    return selectedLineup.positions.filter(pos => pos.formationPosition !== null);
  };


  const getPositionStyle = (position: LineupPosition): React.CSSProperties => {
    const formationPosition = position.formationPosition;

    // Terugval naar het midden voor een positie zonder coördinaten, zoals een wissel.
    if (!formationPosition) {
      return VELD_MIDDEN_STIJL;
    }

    return veldPositieStijl(formationPosition.xPercent, formationPosition.yPercent);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <p>{t('lineup.loading')}</p>
      </div>
    );
  }

  if (errorKey) {
    return (
      <div className="text-center py-8 text-red-600">
        <p>{t(errorKey)}</p>
      </div>
    );
  }

  if (!match || lineups.length === 0) {
    return (
      <div className="text-center py-8">
        <p>{t('lineup.none')}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Lineup Selection Dropdown */}
      {lineups.length > 1 && (
        <div className="mb-4 max-w-[700px] mx-auto">
          <Select
            value={selectedLineup ? { value: selectedLineup.id, label: selectedLineup.name } : null}
            onChange={(selectedOption: SingleValue<{ value: number; label: string }>) => {
              if (selectedOption) {
                const lineup = lineups.find(l => l.id === selectedOption.value);
                if (onLineupChange) {
                  onLineupChange(lineup || null);
                } else {
                  setInternalSelectedLineup(lineup || null);
                }
              }
            }}
            options={lineups.map(lineup => ({
              value: lineup.id,
              label: lineup.name
            }))}
            theme={themaSelectThema}
            styles={{
              control: (provided, state) => ({
                ...provided,
                borderColor: state.isFocused ? 'rgb(var(--merk-inkt))' : 'rgb(var(--vlak-4))',
                boxShadow: state.isFocused ? '0 0 0 2px rgba(139, 208, 244, 0.2)' : 'none',
                fontSize: '16px',
                '&:hover': {
                  borderColor: 'rgb(var(--merk-inkt))',
                },
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected 
                  ? 'rgb(var(--merk-inkt))' 
                  : state.isFocused 
                    ? '#f0f9ff' 
                    : 'rgb(var(--vlak-kaart))',
                color: state.isSelected ? 'rgb(var(--vlak-kaart))' : '#2A1C0D',
                fontSize: '16px',
              }),
              singleValue: (provided) => ({
                ...provided,
                fontSize: '16px',
              }),
              placeholder: (provided) => ({
                ...provided,
                fontSize: '16px',
              }),
              input: (provided) => ({
                ...provided,
                fontSize: '16px',
              }),
            }}
            placeholder={t('lineup.selectPlaceholder')}
            className="text-sm"
          />
        </div>
      )}

      {/* Hockey Field with Players */}
      <div className="flex justify-center">
        <div className="relative inline-block">
          <img
            src={match.isIndoor ? "/indoor-hockey-pitch.png" : "/hockey-field-background.jpg"}
            alt={match.isIndoor ? t('common:field.indoorAlt') : t('common:field.outdoorAlt')}
            className="block max-w-[700px] w-full h-auto dark:brightness-90 dark:saturate-[.9]"
          />
          
          {/* Field Players Positioned on Field */}
          <div className="absolute inset-0">
            {getFieldPlayers()
              .filter((position) => position.player !== null)
              .map((position) => (
                <div
                  key={position.id}
                  style={getPositionStyle(position)}
                >
                  <PlayerCircle
                    player={position.player}
                    position={position.formationPosition?.positionName || 'SUB'}
                    size="medium"
                    showPosition={true}
                    color={position.color}
                  />
                </div>
              ))}
          </div>
        </div>
      </div>

    </div>
  );
};

export default HockeyFieldBackground;